import { useFocusEffect, useNavigation } from 'expo-router';
import { useCallback, useLayoutEffect } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';

import { useGetFavorites } from '@/api/hooks/favoritesHooks';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import ListEmptyComponent from '@/components/ListEmptyComponent';
import NextPageFetchingIndicator from '@/components/NextPageFetchingIndicator';
import FavoriteCard from '@/components/settings/favorites/FavoriteCard';
import Title from '@/components/Title';
import { extractFavoriteDataItem } from '@/utils/favoriteLogic';
import { i18n } from '@/utils/i18n';

const Favorites = () => {
  const navigation = useNavigation();

  //Locales
  const favorites = i18n.t('settings.screen_names.favorites');
  const empty_favorites = i18n.t('favorites.empty_favorites');

  const { data, isPending, isFetching, refetch, hasNextPage, isFetchingNextPage, isRefetching, fetchNextPage } =
    useGetFavorites({
      n: '0',
      limit: 10
    });

  const favoritesItems = data?.pages.flatMap((page) => page.results);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={favorites} />,
      headerLeft: () => <GoBackButton />
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFocusEffect(
    useCallback(() => {
      refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const renderList = () => (
    <FlatList
      style={styles.flatList}
      directionalLockEnabled
      contentContainerStyle={styles.flatListContentContainerStyle}
      columnWrapperStyle={styles.flatListColumnWrapperStyle}
      numColumns={2}
      keyExtractor={(item) => item.id}
      data={favoritesItems}
      showsVerticalScrollIndicator={false}
      showsHorizontalScrollIndicator={false}
      onRefresh={refetch}
      refreshing={isRefetching}
      onEndReachedThreshold={0.6}
      onEndReached={(_) => {
        hasNextPage && !isFetching && fetchNextPage();
      }}
      renderItem={({ item }) => {
        const favoriteData = extractFavoriteDataItem(item);

        if (!favoriteData) return null;

        const { title, image, onPress } = favoriteData;

        return (
          <View style={{ flex: 1 / 2 }}>
            <FavoriteCard title={title} image={image} onPress={onPress ? onPress : () => undefined} />
          </View>
        );
      }}
      ListEmptyComponent={<ListEmptyComponent label={empty_favorites} />}
      ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
    />
  );

  return <View style={styles.container}>{isPending ? <DotsLoader /> : renderList()}</View>;
};

export default Favorites;

const styles = StyleSheet.create({
  flatList: {
    marginTop: 26,
    width: '100%'
  },

  container: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 18,
    height: '100%'
  },

  flatListContentContainerStyle: {
    gap: 25
  },

  flatListColumnWrapperStyle: {
    gap: 22
  }
});
